import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Download, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api.js';
import Swal from 'sweetalert2';

export default function IndiceDocumentosModal({ indice, onClose }) {
  const [documentos, setDocumentos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [subiendo, setSubiendo] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (indice) fetchDocumentos();
  }, [indice]);

  const fetchDocumentos = async () => {
    try {
      setCargando(true);
      const { data } = await api.get(`/indices/${indice.id}/documentos`);
      setDocumentos(Array.isArray(data.documentos) ? data.documentos : []);
    } catch (error) {
      toast.error('Error al cargar los documentos.');
      setDocumentos([]);
    } finally {
      setCargando(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast.error('Solo se permiten archivos PDF.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('El archivo supera los 10MB permitidos.');
      return;
    }

    try {
      setSubiendo(true);
      const formData = new FormData();
      formData.append('documento', file);
      formData.append('registro_indice_id', indice.id);

      await api.post(`/indices/${indice.id}/documentos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Documento subido correctamente');
      fetchDocumentos();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al subir el documento.');
    } finally {
      setSubiendo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (docId) => {
    const result = await Swal.fire({
      title: '¿Eliminar Documento?',
      text: '¿Seguro que deseas eliminar este documento?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;
    try {
      await api.delete(`/indices/${indice.id}/documentos/${docId}`);
      toast.success('Documento eliminado');
      fetchDocumentos();
    } catch (error) {
      toast.error('Error al eliminar el documento.');
    }
  };

  const handleDownload = (doc) => {
    const url = `${api.defaults.baseURL}/documentos/${doc.token_descarga}/descargar`;
    window.open(url, '_blank');
  };

  if (!indice) return null;

  return (
    <div className="modal-overlay">
      <div className="glass-card" style={{ width: '100%', maxWidth: '600px', margin: '2rem', padding: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h3 style={{ margin: 0, color: 'var(--copper)' }}>Documentos del Registro</h3>
            <div className="text-muted" style={{ margin: '0.5rem 0 0', fontSize: '0.9rem' }}>
              <div style={{ fontWeight: 500, color: 'var(--gray-200)', marginBottom: '0.25rem' }}>
                {indice.intervinientes && indice.intervinientes.length > 0 
                  ? indice.intervinientes.map(inv => [inv.nombres, inv.apellido_paterno, inv.apellido_materno].filter(Boolean).join(' ')).join(' / ')
                  : [indice.primer_nombre, indice.apellido_paterno, indice.apellido_materno].filter(Boolean).join(' ') || 'Sin nombre'}
              </div>
              <div>
                {indice.tipo_registro || indice.tipo_actuacion} — 
                <span style={{ marginLeft: '0.5rem' }}>
                  Foja: <strong>{indice.foja || '—'}</strong>, 
                  Nº: <strong>{indice.numero || '—'}</strong>, 
                  Año: <strong>{indice.anio || '—'}</strong>
                </span>
              </div>
            </div>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={onClose} style={{ padding: '0.5rem' }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
          <input
            type="file"
            accept="application/pdf"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <button 
            className="btn btn-primary btn-sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={subiendo || cargando}
          >
            {subiendo ? <span className="spinner" /> : <Upload size={16} />} 
            {subiendo ? 'Subiendo...' : 'Subir Nuevo PDF'}
          </button>
        </div>

        {cargando ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--gray-400)' }}>
            <span className="spinner" style={{ margin: '0 auto 1rem', display: 'block' }} />
            Cargando documentos...
          </div>
        ) : documentos.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '3rem', border: '1px dashed rgba(255,255,255,0.1)', borderRadius: '12px' }}>
            <p className="text-muted">No hay documentos adjuntos a este registro.</p>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {documentos.map(doc => (
              <li key={doc.id} style={{ 
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '1rem', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', overflow: 'hidden' }}>
                  <div style={{ color: 'var(--copper)' }}>📄</div>
                  <div style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{doc.nombre_archivo}</span>
                    <br/>
                    <small className="text-muted">{new Date(doc.created_at).toLocaleDateString()}</small>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(doc)} style={{ padding: '0.5rem' }} title="Descargar">
                    <Download size={16} />
                  </button>
                  <button className="btn btn-error btn-sm" onClick={() => handleDelete(doc.id)} style={{ padding: '0.5rem' }} title="Eliminar">
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
